/**
 * Login Dialog Component
 * Modal dialog for user authentication
 */

export interface LoginDialogEvents {
    onLogin?: (username: string, password: string) => void;
    onCancel?: () => void;
}

export class LoginDialog {
    private overlay: HTMLElement | null = null;
    private events: LoginDialogEvents;
    private isVisible = false;
    private isLoading = false;

    constructor(events: LoginDialogEvents = {}) {
        this.events = events;
    }

    /**
     * Show the login dialog
     */
    show(): void {
        if (this.isVisible) return;

        this.createDialog();
        this.isVisible = true;

        // Focus username input
        setTimeout(() => {
            const usernameInput = this.overlay?.querySelector('#login-username') as HTMLInputElement;
            usernameInput?.focus();
        }, 100);
    }

    /**
     * Hide the login dialog
     */
    hide(): void {
        if (!this.isVisible || !this.overlay) return;

        this.overlay.classList.add('fade-out');

        setTimeout(() => {
            this.overlay?.remove();
            this.overlay = null;
            this.isVisible = false;
        }, 200);
    }

    /**
     * Show error message
     */
    showError(message: string): void {
        const errorEl = this.overlay?.querySelector('#login-error') as HTMLElement;
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.style.display = 'block';
        }
        this.setLoading(false);
    }

    /**
     * Set loading state
     */
    setLoading(loading: boolean): void {
        this.isLoading = loading;

        const submitBtn = this.overlay?.querySelector('#login-submit') as HTMLButtonElement;
        const inputs = this.overlay?.querySelectorAll('input') as NodeListOf<HTMLInputElement>;

        if (submitBtn) {
            submitBtn.disabled = loading;
            submitBtn.innerHTML = loading
                ? '<span class="spinner"></span> מתחבר...'
                : '🔐 התחבר';
        }

        inputs?.forEach(input => {
            input.disabled = loading;
        });
    }

    // =========================================================================
    // Private Methods
    // =========================================================================

    private createDialog(): void {
        this.overlay = document.createElement('div');
        this.overlay.className = 'dialog-overlay';
        this.overlay.innerHTML = `
            <div class="dialog login-dialog">
                <div class="dialog-header">
                    <h2 class="dialog-title">🔐 התחברות למערכת</h2>
                </div>

                <form id="login-form" class="dialog-content">
                    <div id="login-error" class="error-message" style="display: none;"></div>

                    <div class="form-group">
                        <label for="login-username">שם משתמש</label>
                        <input
                            type="text"
                            id="login-username"
                            name="username"
                            placeholder="הכנס שם משתמש"
                            required
                            autocomplete="username"
                        />
                    </div>

                    <div class="form-group">
                        <label for="login-password">סיסמה</label>
                        <input
                            type="password"
                            id="login-password"
                            name="password"
                            placeholder="הכנס סיסמה"
                            required
                            autocomplete="current-password"
                        />
                    </div>

                    <div class="form-group checkbox-group">
                        <label>
                            <input type="checkbox" id="login-remember" name="remember" />
                            <span>זכור אותי</span>
                        </label>
                    </div>
                </form>

                <div class="dialog-footer">
                    <button type="button" id="login-cancel" class="btn btn-secondary">
                        ביטול
                    </button>
                    <button type="submit" form="login-form" id="login-submit" class="btn btn-primary">
                        🔐 התחבר
                    </button>
                </div>
            </div>
        `;

        document.body.appendChild(this.overlay);
        this.attachEventListeners();

        // Animate in
        requestAnimationFrame(() => {
            this.overlay?.classList.add('visible');
        });
    }

    private attachEventListeners(): void {
        if (!this.overlay) return;

        // Form submit
        const form = this.overlay.querySelector('#login-form') as HTMLFormElement;
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleSubmit();
        });

        // Cancel button
        const cancelBtn = this.overlay.querySelector('#login-cancel') as HTMLButtonElement;
        cancelBtn.addEventListener('click', () => {
            this.events.onCancel?.();
            this.hide();
        });

        // Click outside to close
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.events.onCancel?.();
                this.hide();
            }
        });

        // Escape key to close
        document.addEventListener('keydown', this.handleKeyDown);
    }

    private handleKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'Escape' && this.isVisible && !this.isLoading) {
            this.events.onCancel?.();
            this.hide();
        }
    };

    private handleSubmit(): void {
        if (this.isLoading) return;

        const usernameInput = this.overlay?.querySelector('#login-username') as HTMLInputElement;
        const passwordInput = this.overlay?.querySelector('#login-password') as HTMLInputElement;
        const errorEl = this.overlay?.querySelector('#login-error') as HTMLElement;

        const username = usernameInput?.value.trim();
        const password = passwordInput?.value;

        // Clear previous error
        if (errorEl) {
            errorEl.style.display = 'none';
        }

        // Validate
        if (!username || !password) {
            this.showError('אנא מלא את כל השדות');
            return;
        }

        this.setLoading(true);
        this.events.onLogin?.(username, password);
    }
}

/**
 * Toast Notification Component
 */
export class Toast {
    private container: HTMLElement | null = null;

    constructor() {
        this.createContainer();
    }

    show(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info', duration: number = 3000): void {
        if (!this.container) {
            this.createContainer();
        }

        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;

        const icon = this.getIcon(type);
        toast.innerHTML = `
            <span class="toast-icon">${icon}</span>
            <span class="toast-message">${message}</span>
            <button class="toast-close">×</button>
        `;

        // Add to container
        this.container!.appendChild(toast);

        // Animate in
        requestAnimationFrame(() => {
            toast.classList.add('visible');
        });

        // Close button
        const closeBtn = toast.querySelector('.toast-close');
        closeBtn?.addEventListener('click', () => {
            this.removeToast(toast);
        });

        // Auto remove
        if (duration > 0) {
            setTimeout(() => {
                this.removeToast(toast);
            }, duration);
        }
    }

    private createContainer(): void {
        this.container = document.createElement('div');
        this.container.className = 'toast-container';
        document.body.appendChild(this.container);
    }

    private removeToast(toast: HTMLElement): void {
        toast.classList.remove('visible');
        toast.classList.add('fade-out');

        setTimeout(() => {
            toast.remove();
        }, 200);
    }

    private getIcon(type: string): string {
        switch (type) {
            case 'success': return '✓';
            case 'warning': return '⚠';
            case 'error': return '✕';
            default: return 'ℹ';
        }
    }
}
